import Clarity from '@microsoft/clarity';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Cafe, Table, TablesMap } from '../apiTypes';
import Loader from '../components/Loader';
import { useDocument } from '../utils/api';
import currency from '../utils/currency';
import { getInitMap } from '../utils/map';
import { useOnline } from '../utils/useOnline';
import { useUser } from './user.context';

export type Params = {
  cafeSlug: string;
};

type AdminContextValue = {
  cafe: DbDocument<Cafe> | null | undefined;
  tablesMap: TablesMap;
  cafeSlug: string;
  tablesByIds: Record<string, Table>;
  timeForWaiting: number;
  getCurrency: (sum?: number | string) => string;
  isLite: boolean;
  isPlus: boolean;
  isPro: boolean;
  online: boolean;
};

export const initTablesMap: TablesMap = {
  tables: [],
  enabled: false,
  scale: 50,
  bgUrl: '',
};

const AdminContext = createContext<AdminContextValue | null>(null);

export const useAdmin = (): AdminContextValue => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within a AdminProvider');
  }

  return context;
};

export const AdminProvider = ({ children }: PropsWithChildren) => {
  const { online } = useOnline();
  const navigate = useNavigate();
  const { cafeSlug = '' } = useParams<Params>();
  const {
    cafeIds,
    isLoading,
    user: { id, displayName },
  } = useUser();

  const { data: cafe, isValidating: cafeIsValidating } = useDocument<Cafe>(id ? `cafes/${cafeSlug}` : null);

  const tablesByIds = useMemo(
    () => (cafe?.tablesMap?.tables || []).reduce((acc, t) => ({ ...acc, [t.id]: t }), getInitMap<Table>()),
    [cafe?.tablesMap?.tables]
  );

  const getCurrency = useCallback(
    (sum?: number | string) => currency(Number(sum || 0), cafe?.currency),
    [cafe?.currency]
  );

  useEffect(() => {
    if (!isLoading && !cafeIsValidating && !cafe?.name) {
      navigate('/');
    }
  }, [cafe?.name, cafeIsValidating, isLoading, navigate]);

  useEffect(() => {
    if (
      !isLoading &&
      !cafeIsValidating &&
      cafe?.id &&
      !cafeIds?.includes(cafe.id) &&
      id !== 'max.popov.mail@gmail.com'
    ) {
      navigate('/');
    }
  }, [cafe?.id, cafeIds, cafeIsValidating, id, isLoading, navigate]);

  const value: AdminContextValue = useMemo(
    () => ({
      cafe,
      tablesMap: cafe?.tablesMap || initTablesMap,
      cafeSlug,
      tablesByIds,
      timeForWaiting: 3,
      getCurrency,
      isLite:
        cafe?.plan?.type !== 'plus' &&
        cafe?.plan?.type !== 'pro' &&
        (cafe?.plan?.lastDate || 1709247600000) > Date.now(),
      isPlus: cafe?.plan?.type === 'plus' && (cafe?.plan?.lastDate || 0) > Date.now(),
      isPro: cafe?.plan?.type === 'pro' && (cafe?.plan?.lastDate || 0) > Date.now(),
      online,
    }),
    [cafe, cafeSlug, getCurrency, tablesByIds, online]
  );

  useEffect(() => {
    if (cafe?.name) {
      Clarity.setTag('cafe', cafe.name);
    }
  }, [cafe?.name, displayName]);

  if (cafeIsValidating || !cafe?.name || (!cafeIds?.includes(cafe?.id) && id !== 'max.popov.mail@gmail.com')) {
    return <Loader open fullScreen />;
  }

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};
